import PusherBatchAuthorizer from 'pusher-js-auth'
import Pusher, {
  type Options,
  type AuthorizerOptions,
  type PresenceChannel,
  type Channel,
} from 'pusher-js'
import { makeAutoObservable } from 'mobx'
import { PUSHER_DRIVER, GET_API_URL, SOKETI_KEY, SOKETI_CLUSTER, SOKETI_HOST } from 'shared/config'
import { CheckInternet } from 'shared/store/CheckInternet'
import { get_url_wss } from 'shared/config/lib/getConfigUrls'
import { sentEventSentry } from 'entities/Sentry/lib/sentEventSentry'
import { eventEmitterStore } from 'entities/EventEmitter'
import type { IWebsocketAction } from './type'

interface ISettingsWebsocket {
  SHOULD_LOG: boolean
  KEY: string
}

interface IOptions extends Options, AuthorizerOptions {
  broadcaster?: string
}

export class Websocket {
  private _accessToken?: string | null = null
  private _socket: Pusher
  private _settings: ISettingsWebsocket
  private _channels: Map<string, PresenceChannel | Channel> = new Map()
  private _worker = new Worker('/worker.js')

  constructor() {
    this._settings = {
      SHOULD_LOG: false,
      KEY: SOKETI_KEY,
    }

    Pusher.logToConsole = this._settings.SHOULD_LOG
    Pusher.log = this.log

    this._socket = new Pusher(this._settings.KEY, this.config)

    delete window.Pusher

    this._worker.onmessage = (e) => {
      if (e.data.action === 'detect_fast') {
        if (CheckInternet.isOnline) {
          if (this._socket.connection.state === 'unavailable') {
            this.reconnect()
          }
        }
      }
    }

    this.eventsConnection()

    makeAutoObservable(this)
  }

  eventsConnection = () => {
    this._socket.connection.bind('error', (err: never) => {
      sentEventSentry({
        event: '[Websocket:Error] EventsConnection',
        error: new Error(JSON.stringify(err)),
        name: 'websocket',
        level: 'error',
      })

      this.onError(err)
      eventEmitterStore.emit('websocket:error')
    })

    this._socket.connection.bind('connected', () => {
      this.onConnected()
      eventEmitterStore.emit('websocket:connected')
    })

    this._socket.connection.bind('unavailable', () => {
      sentEventSentry({
        event: '[Websocket:Unavailable] EventsConnection',
        name: 'websocket',
        level: 'error',
      })

      this.onConnected()
      eventEmitterStore.emit('websocket:unavailable')
    })

    this._socket.connection.bind('failed', (status: never) => {
      sentEventSentry({
        event: '[Websocket:Failed] EventsConnection',
        error: new Error(status),
        name: 'websocket',
        level: 'error',
      })

      console.error('Socket failed', status)
      eventEmitterStore.emit('websocket:failed')
      window.location.reload()
    })
  }

  connect(handler?: (_this: Pusher) => void) {
    this.updateToken()

    eventEmitterStore.subscribe('websocket:connected', () => {
      handler?.(this._socket)
    })

    if (this.isConnected) {
      handler?.(this._socket)
    }
  }

  reconnect = () => {
    this._socket.disconnect()
    this._socket.connect()
  }

  disconnect() {
    this._socket.disconnect()
  }

  reinit = () => {
    this.disconnect()
    this._socket = new Pusher(this._settings.KEY, this.config)
    this.eventsConnection()
  }

  updateToken = () => {
    this._accessToken = window.localStorage.getItem('token')

    if (this._accessToken) {
      this._socket.config.auth = {
        headers: {
          Authorization: `Bearer ${this._accessToken}`,
        },
      }
    }

    return this._accessToken
  }

  subscribe(channelName?: string) {
    if (!channelName) return

    const token = this.updateToken()

    if (!token) return console.error('Token does not exist:', this)

    this._channels.set(channelName, this._socket.subscribe(channelName))
  }

  unsubscribe(channelName: string) {
    const channel = this.getChannel(channelName)

    if (channel) {
      this._socket.unsubscribe(channelName)
      this._channels.delete(channelName)
    }
  }

  getChannel(channelName: string) {
    return this._channels.get(channelName)
  }

  on<E extends keyof IWebsocketAction>(event: E, callback: (data: IWebsocketAction[E]) => void) {
    this._socket.bind(event, callback)

    return () => this.off(event, callback)
  }

  off<E extends keyof IWebsocketAction>(event: E, callback?: (data: IWebsocketAction[E]) => void) {
    this._socket.unbind(event, callback)
  }

  sendEvent = <T>(channelName: string, event: string, data: T) => {
    const channel = this.getChannel(channelName)
    if (channel) {
      channel.trigger(event, data)
    }
  }

  onError = (err: never) => {
    this.log('Error: ', err)
  }

  onConnected = () => {
    this.log('Connected: ')
  }

  log = (message: string, ...args: never[]) => {
    if (this._settings.SHOULD_LOG) {
      console.log(`[WS] ${message}`, args)
    }
  }

  get isConnected() {
    return this._socket.connection.state === 'connected'
  }

  get socket() {
    return this._socket
  }

  get config(): IOptions {
    const options: IOptions = {
      authorizer: PusherBatchAuthorizer,
      authDelay: 200,
      broadcaster: 'pusher',
      authEndpoint: `${GET_API_URL()}broadcasting/auth?connection=${PUSHER_DRIVER}`,
    }

    if (PUSHER_DRIVER === 'soketi') {
      options.wsHost = get_url_wss(SOKETI_HOST)
      options.enabledTransports = ['ws', 'wss']
      options.disableStats = true
      options.forceTLS = false
    }

    if (PUSHER_DRIVER === 'pusher') {
      options.cluster = SOKETI_CLUSTER
    }

    return options
  }
}
